import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';

interface RequestAuditLogsDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

interface AuditLogEntry {
  _id: string;
  id?: string;
  request_id?: string;
  entity_type?: string;
  entity_id?: string;
  action?: string;
  field?: string;
  old_value?: any;
  new_value?: any;
  changed_by?: string;
  changed_by_name?: string;
  changed_at?: string;
  details?: any;
}

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

const defaultPagination: PaginationState = {
  page: 1,
  limit: 50,
  total: 0,
  pages: 0,
  has_next: false,
  has_prev: false
};

const RequestAuditLogsDashboard: React.FC<RequestAuditLogsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination);

  const fetchLogs = useCallback(async (
    page: number = 1,
    limit: number = defaultPagination.limit,
    search: string = '',
    columnFilters: Record<string, string> = {}
  ) => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString()
      });

      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      // Apply column filters from DataDashboard
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value && value.trim()) {
          // Map column keys to API parameter names
          const paramMap: Record<string, string> = {
            'request_id': 'request_id',
            'action': 'action',
            'changed_by_name': 'changed_by',
            'changed_by': 'changed_by',
            'field': 'field',
            'company_request_id': 'company_request_id'
          };
          const paramName = paramMap[key] || key;
          params.append(paramName, value.trim());
        }
      });

      const response = await fetch(`${API_ENDPOINTS.REQUEST_AUDIT_LOGS}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load logs (${response.status})`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to load logs');
      }

      const data: AuditLogEntry[] = (result.data || []).map((item: AuditLogEntry) => ({
        ...item,
        id: item._id || item.id
      }));

      setLogs(data);

      const total = result.pagination?.total || 0;
      const computedLimit = result.pagination?.limit || limit;
      const computedSkip = result.pagination?.skip || skip;
      const pageNumber = Math.floor(computedSkip / computedLimit) + 1;
      const totalPages = computedLimit > 0 ? Math.ceil(total / computedLimit) : 0;

      setPagination({
        page: pageNumber,
        limit: computedLimit,
        total,
        pages: totalPages,
        has_next: computedSkip + computedLimit < total,
        has_prev: computedSkip > 0
      });
    } catch (err: any) {
      console.error('Error fetching request audit logs:', err);
      setError(err.message || 'Failed to load logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs(1, pagination.limit, '', {});
  }, [fetchLogs]);

  const handleDataFetch = (params: {
    page: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    columnFilters?: Record<string, string>;
  }) => {
    const nextLimit = params.limit || pagination.limit;
    fetchLogs(params.page, nextLimit, params.search || '', params.columnFilters || {});
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    if (typeof value === 'string' && value.length > 100) {
      return value.substring(0, 100) + '...';
    }
    return String(value);
  };

  const columns = useMemo(() => ([
    {
      key: 'changed_at',
      title: 'Date & Time',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '180px',
      render: (value: string) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'medium'
          }).format(date);
        } catch {
          return value;
        }
      }
    },
    {
      key: 'action',
      title: 'Action',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '150px',
      render: (value: string) => {
        if (!value) return '-';
        const actionMap: Record<string, string> = {
          'created': 'Created',
          'updated': 'Updated',
          'phase_changed': 'Phase Changed',
          'approved': 'Approved',
          'rejected': 'Rejected',
          'supplier_selected': 'Supplier Selected',
          'quotes_updated': 'Quotes Updated',
          'product_added': 'Product Added',
          'product_updated': 'Product Updated',
          'product_deleted': 'Product Deleted',
          'email_sent': 'Email Sent',
          'email_received': 'Email Received',
          'deleted': 'Deleted',
          'split': 'Split',
          'user_created': 'User Created',
          'user_updated': 'User Updated',
          'user_rights_updated': 'User Rights Updated',
          'role_updated': 'Role Updated'
        };
        return actionMap[value] || value;
      }
    },
    {
      key: 'entity_type',
      title: 'Entity Type',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '120px',
      render: (value: string) => {
        if (!value) return '-';
        const typeMap: Record<string, string> = {
          'request': 'Request',
          'user': 'User',
          'role': 'Role',
          'product': 'Product',
          'supplier': 'Supplier'
        };
        return typeMap[value] || value;
      }
    },
    {
      key: 'request_id',
      title: 'Request ID',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '120px',
      render: (value: string) => {
        if (!value) return '-';
        const idStr = String(value);
        return idStr.length > 12 ? idStr.substring(0, 12) + '...' : idStr;
      }
    },
    {
      key: 'entity_id',
      title: 'Entity ID',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '120px',
      render: (value: string) => {
        if (!value) return '-';
        const idStr = String(value);
        return idStr.length > 12 ? idStr.substring(0, 12) + '...' : idStr;
      }
    },
    {
      key: 'company_request_id',
      title: 'Company Request ID',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '150px',
      render: (value: any) => {
        if (!value && value !== 0) return '-';
        return String(value);
      }
    },
    {
      key: 'changed_by_name',
      title: 'Changed By',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '150px'
    },
    {
      key: 'field',
      title: 'Field',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '150px'
    },
    {
      key: 'old_value',
      title: 'Old Value',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '200px',
      render: (value: any) => {
        return <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '12px', maxHeight: '100px', overflow: 'auto' }}>{formatValue(value)}</pre>;
      }
    },
    {
      key: 'new_value',
      title: 'New Value',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '200px',
      render: (value: any) => {
        return <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '12px', maxHeight: '100px', overflow: 'auto' }}>{formatValue(value)}</pre>;
      }
    },
    {
      key: 'details',
      title: 'Details',
      type: 'text' as const,
      sortable: false,
      editable: false,
      width: '250px',
      render: (value: any) => {
        if (!value || Object.keys(value).length === 0) return '-';
        try {
          return <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '12px', maxHeight: '150px', overflow: 'auto' }}>{JSON.stringify(value, null, 2)}</pre>;
        } catch {
          return String(value);
        }
      }
    }
  ]), []);

  const transformedLogs = useMemo(() => (
    logs.map(log => ({
      ...log,
      id: log.id || log._id
    }))
  ), [logs]);

  return (
    <div className="request-audit-logs-dashboard">

      <DataDashboard
        title="Audit Logs"
        collection="request_audit_logs"
        data={transformedLogs}
        columns={columns}
        isLoading={isLoading}
        error={error}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataFetch={handleDataFetch}
        onDataChange={setLogs}
        showAddButton={false}
        showSearch={true}
        showSelection={false}
        showEditButton={false}
        showDeleteButton={false}
        layoutType="admin"
        paginationInfo={pagination}
      />
    </div>
  );
};

export default RequestAuditLogsDashboard;

